import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { brandsAPI, DEFAULT_BRANDS } from '../lib/api';

const BrandContext = createContext(null);

export const BrandProvider = ({ children }) => {
  const [brands, setBrands] = useState(DEFAULT_BRANDS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch brands from API
    const fetchBrands = async () => {
      setLoading(true);
      try {
        const fetchedBrands = await brandsAPI.getAll();
        if (fetchedBrands && fetchedBrands.length > 0) {
          setBrands(fetchedBrands);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
        // Keep default brands on error
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  const getBrand = useCallback((slug) => {
    return brands.find(b => b.slug === slug) || brands[0];
  }, [brands]);

  const getBrandColor = useCallback((slug, colorType = 'primary') => {
    const brand = getBrand(slug);
    return brand?.colors?.[colorType] || '#CCCCCC';
  }, [getBrand]);

  const getBrandFont = useCallback((slug) => {
    const brand = getBrand(slug);
    return brand?.font || 'body';
  }, [getBrand]);

  const value = useMemo(() => ({
    brands,
    loading,
    getBrand,
    getBrandColor,
    getBrandFont
  }), [brands, loading, getBrand, getBrandColor, getBrandFont]);

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrands = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrands must be used within a BrandProvider');
  }
  return context;
};
